/**********************************************************
Hawaiian Macadamia Nut Services
Alexander Self
7/15/20
Irrigation utils: misc utility functions used through the module
**********************************************************/
import moment from 'moment';
/**
* generateID(): Generates a unique general purpose ID of any length
* @param {Number} len: length of unqiue string
* @returns {String} uuid: unique id
* @called-in: ['irrigation_actions.js]
**/
export const generateID = (len) => {
  if (len === 0) return;
  let xString = '';
  for (let i = 0; i < len; i++) xString += 'x';
  var d = new Date().getTime();
  if(window.performance && typeof window.performance.now === "function"){
      d += performance.now(); //use high-precision timer if available
  }
  var uuid = xString.replace(/[xy]/g, function(c) {
      var r = (d + Math.random()*16)%16 | 0;
      d = Math.floor(d/16);
      // eslint-disable-next-line
      return (c=='x' ? r : (r&0x3|0x8)).toString(16);
  });
  return uuid;
};


/**
* packageForm(): Packages form element values with dashes in ids
* @param {String} id, id of form
* @returns {Object} formObj, key value form object
* @called-in: ['irrigation_create.js']
**/
export function packageForm(id) {
  let elements = document.getElementById(`${id}`).elements;
  let formObj = {};
  // eslint-disable-next-line
  for (let i = 0, element; element = elements[i++];) {
    formObj[element.id.split('-')[1]] = element.value;
  }

  return formObj;
}

/**
* handleLog(): Handles valve, meter, engine log updates
* @param {Object} payload, log object(engine, meter, valve)
* @param {Array} log, state log array
* @param {String} prop, property for to measure against diff cases
* @param {String} id, id of engine, meter or valve object
* @returns {Array} newLog, new state log array
* @called-in: ['reducer.js']
**/
export function handleLog({ payload, log, prop, id }) {
  let newLog = [ ...log ];
  // Case 1: Does the log have hours > 0 and does it exist in the newLog array
  if (Number(payload[prop]) > 0 && !newLog.find((v) => v[id] === payload[id])) newLog = newLog.concat(payload);
  // Case 2: Does the log have a value === 0 and exists in the newLog array
  if (Number(payload[prop]) === 0 && newLog.find((v) => v[id] === payload[id])) {
    newLog = newLog.filter((v) => v[id] !== payload[id]);
  }
  // Case 3: Does the obj exist in newLog array and was the value changed
  if (Number(payload[prop]) > 0 && newLog.find((v) => v[id] === payload[id])) {
    let _v = newLog.find((v) => v[id] === payload[id]);
    if (Number(_v[prop]) !== Number(payload[prop])) {
      newLog = newLog.filter((v) => v[id] !== payload[id]).concat({ ..._v, ...payload });
    }
  }

  return newLog;
}

/**************** Calculations ****************/
/**
* calculateTotalFuelGallons()
* @param {Number} hours: engine hours run
* @param {Number} galPerHour: fuel gallons consumed per hour
* @returns {Number} total gallons consumed
* @called-in: ['irrigation_engine_hours.js']
**/
export function calculateTotalFuelGallons({hours, galPerHour}) {
  if (!(hours, galPerHour)) return 0.00;
  return Number(hours * galPerHour);
}

/**
* calculateTotalGallons()
* @param {Number} hours: valve hours run
* @param {Number} galPerMinute: gallons of water per minute
* @returns {Number} total gallons released
* @called-in: ['irrigation_valve_hours.js']
**/
export function calculateTotalGallons({hours, galPerMinute}) {
  if (!(hours, galPerMinute)) return 0.00;
  return Number(hours * galPerMinute * 60);
}


/* Sums a selected property of an array */
export function sumProp(prop, arr) {
  if (!(prop && arr)) return 0.00;
  let total = arr.reduce((acc, cur) => { return acc + Number(cur[prop]) }, 0);
  return total.toFixed(2);
}

export function avgProp(prop, arr) {
  if (!(prop && arr)) return 0.00;
  let total = arr.reduce((acc, cur) => { return acc + Number(cur[prop]) }, 0);
  return (total / arr.length).toFixed(2);
}

/**
* preFillForm(): Prefills a form in the irrigation module
* @param {Number} data: form data
* @param {String} formId: id of the form to prefill
* @param {String} prop: custom prop to set element value to
* @returns
* @called-in: ['irrigation_actions.js']
**/
export function preFillForm({data, formId, prop, style}) {
  let form = document.getElementById(formId).elements;

  // eslint-disable-next-line
  for (let i = 0, element; element = form[i++];) {
    if (element.name === "") continue;
    let id = element.name.split('-')[1];
    let _d = data.find((d) => Number(d.id) === Number(id));
    if (_d) element.value = _d[prop];
    if (_d && style) {
      element.style.backgroundColor = '#bbdefb';
      element.style.fontWeight = 'bold';
      element.style.textAlign = 'center';
      element.style.color = '#424242';
    }
  }
  return;
}

/**
* preFillMeterForm(): Prefills the meter form in the irrigation module. Special case for multiple inputs
* @param {Number} data: form data
* @param {String} formId: id of the form to prefill
* @returns
* @called-in: ['irrigation_actions.js']
**/
export function preFillMeterForm({data, formId}) {
  let form = document.getElementById(formId).elements;

  // eslint-disable-next-line
  for (let i = 0, element; element = form[i++];) {
    let id = element.name.split('-')[1];
    if (!id) {
      let _id = element.id.split('-')[0];
      let newId = _id.replace('meter', '');
      id = newId;
    }
    let _d = data.find((d) => Number(d.meterId) === Number(id));
    if (_d) {
      for (let key in _d) {
        let inputKey = element.id.split('-')[1];
        if (inputKey === key) element.value = _d[key];
        if (inputKey === key && key === 'totalGal') {
          element.style.backgroundColor = '#bbdefb';
          element.style.fontWeight = 'bold';
          element.style.textAlign = 'center';
          element.style.color = '#424242';
        }
      }
    }
  }
  return;
}

/**
* clearForm(): Clears a form based on form id; resets any css on inputs
* @param {String} formId: id of the form to prefill
* @returns
* @called-in: ['irrigation_actions.js']
**/
export function clearForm(formId) {
  let form = document.getElementById(formId).elements;

  // eslint-disable-next-line
  for (let i = 0, element; element = form[i++];) {
    let dom = document.getElementById(element.id);
    dom.style.backgroundColor = '#ffffff';
    dom.style.textAlign = 'initial';
    dom.style.fontWeight = 'normal';
  }

  return document.getElementById(formId).reset();
}


/**
* filterOldRecords(): Filters records that are older than two weeks. Uses date property
* @param {Object} r: record with date prop
* @returns {Bool} filtered condition
* @called-in: ['/components/chem_app/chem_app_create.js]
**/
export function filterOldRecords(r) {
  return (Math.abs(moment(r.date).week() - moment().week()) <= 1);
}










/* END */



// WEBPACK FOOTER //
// ./src/irrigation/irrigation_utils.js